import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Settings, Save, Calculator, Loader2, Pencil, X as XIcon, RotateCcw, FileText, Upload, Download, Trash2, Eye, FileCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Liste des variables reconnues par le moteur de calcul (affichée à titre d'aide)
const VARIABLES = [
    'Camion 20 m³', 'Camion 30 m³', 'Distance',
    'Cartons std', 'Cartons livres', 'Cartons verres', 'Cartons bout',
    'Adhésif', 'Bulle 100m x 50cm', 'Bulle 10m x 1m', 'Autre fourniture',
    'Tarif déménageur', 'Panier', 'Hôtel',
    'Prix AOT départ', 'Prix AOT arrivée',
    'Prix Monte-meuble départ', 'Prix Monte-meuble arrivée',
    'Marge',
];

export function AdminSettingsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [settings, setSettings] = useState(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draftFormula, setDraftFormula] = useState('');
    const [draftTva, setDraftTva] = useState('');

    // Documents state
    const [assurance, setAssurance] = useState(null);
    const [uploadingAssurance, setUploadingAssurance] = useState(false);
    const assuranceInputRef = useRef(null);

    // PDF preview modal
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewTitle, setPreviewTitle] = useState('');
    const [previewDownloadType, setPreviewDownloadType] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        axios.get(`${API}/api/admin/price-settings`, { withCredentials: true })
            .then(res => setSettings(res.data))
            .catch(() => toast.error('Erreur chargement paramètres'));
        axios.get(`${API}/api/admin/documents/assurance`, { withCredentials: true })
            .then(res => setAssurance(res.data?.filename ? res.data : null))
            .catch(() => {});
    }, []);

    if (user?.role !== 'admin') {
        return <Navigate to="/" replace />;
    }

    const startEdit = () => {
        setDraftFormula(settings.formula_ht || '');
        setDraftTva(String(settings.tva_rate ?? ''));
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setDraftFormula('');
        setDraftTva('');
    };

    const handleSave = async () => {
        if (!draftFormula.trim()) {
            toast.error('La formule ne peut pas être vide');
            return;
        }
        setSaving(true);
        try {
            const payload = { formula_ht: draftFormula };
            const tvaNum = parseFloat(draftTva);
            if (!isNaN(tvaNum)) payload.tva_rate = tvaNum;
            const { data } = await axios.put(`${API}/api/admin/price-settings`, payload, { withCredentials: true });
            setSettings(data);
            setEditing(false);
            toast.success('Formule enregistrée');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally { setSaving(false); }
    };

    const handleReset = async () => {
        if (!window.confirm('Restaurer la formule par défaut ?')) return;
        setSaving(true);
        try {
            const { data } = await axios.post(`${API}/api/admin/price-settings/reset`, {}, { withCredentials: true });
            setSettings(data);
            setEditing(false);
            toast.success('Formule réinitialisée');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la réinitialisation');
        } finally { setSaving(false); }
    };

    // ---- Documents handlers ----
    const handleAssuranceUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
            toast.error('Seuls les fichiers PDF sont acceptés');
            return;
        }
        // Protection : le fichier ressemble-t-il a un document genere par l'app
        // (inventaire, devis, facture, calcul mensuel) ? Si oui, demander confirmation
        // pour eviter qu'un mauvais fichier soit upload par erreur.
        const suspiciousPatterns = /^(inventaire[_-]|devis[_-]|facture[_-]|calcul[_-]mensuel|aot[_-]|monte[_-]meuble)/i;
        if (suspiciousPatterns.test(file.name)) {
            const ok = window.confirm(
                `Le fichier "${file.name}" semble être un document généré par l'application (inventaire, devis, facture...).\n\n` +
                `Êtes-vous sûr que c'est bien l'ATTESTATION D'ASSURANCE de votre compagnie ?\n\n` +
                `Cliquez sur Annuler si vous voulez choisir un autre fichier.`
            );
            if (!ok) {
                if (assuranceInputRef.current) assuranceInputRef.current.value = '';
                return;
            }
        }
        setUploadingAssurance(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await axios.post(`${API}/api/admin/documents/assurance`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setAssurance(data);
            toast.success('Attestation d\'assurance téléversée');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors du téléversement');
        } finally {
            setUploadingAssurance(false);
            if (assuranceInputRef.current) assuranceInputRef.current.value = '';
        }
    };

    const handleAssuranceDownload = () => {
        // Cache-busting via timestamp pour forcer le navigateur à recharger
        // le fichier après un remplacement (sinon il sert l'ancienne version cachée).
        const ts = assurance?.uploaded_at ? new Date(assurance.uploaded_at).getTime() : Date.now();
        window.open(`${API}/api/admin/documents/assurance/file?t=${ts}`, '_blank');
    };

    const handleAssuranceDelete = async () => {
        if (!window.confirm('Supprimer le document d\'assurance ?')) return;
        try {
            await axios.delete(`${API}/api/admin/documents/assurance`, { withCredentials: true });
            setAssurance(null);
            toast.success('Document supprimé');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    // Présentation — supprimée (remplacée par un bouton de génération à la volée)

    // PDF preview
    const openPreview = async (docType, title) => {
        setPreviewTitle(title);
        setPreviewDownloadType(docType);
        setPreviewLoading(true);
        setPreviewUrl(null);
        try {
            const ts = Date.now();
            const { data } = await axios.get(`${API}/api/admin/documents/${docType}/file?t=${ts}`, {
                withCredentials: true,
                responseType: 'blob',
            });
            const blob = new Blob([data], { type: 'application/pdf' });
            setPreviewUrl(URL.createObjectURL(blob));
        } catch (err) {
            toast.error('Impossible de charger l\'aperçu');
            setPreviewTitle('');
            setPreviewDownloadType(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const downloadFromPreview = () => {
        if (!previewDownloadType) return;
        const ts = Date.now();
        window.open(`${API}/api/admin/documents/${previewDownloadType}/file?t=${ts}`, '_blank');
    };

    const closePreview = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setPreviewTitle('');
        setPreviewDownloadType(null);
    };

    const formatSize = (b) => {
        if (!b) return '';
        if (b < 1024) return `${b} o`;
        if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
        return `${(b / 1024 / 1024).toFixed(2)} Mo`;
    };

    const formatDate = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        } catch { return ''; }
    };

    return (
        <div className="p-6 lg:p-8" data-testid="admin-settings-page">
            <div className="flex items-center gap-3 mb-8">
                <Button variant="outline" size="sm" onClick={() => navigate('/admin')} data-testid="back-to-admin-btn">
                    <ArrowLeft size={16} className="mr-1.5" /> Retour
                </Button>
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Settings size={24} className="inline-block mr-2 mb-1 text-slate-600" />
                        Paramètres
                    </h1>
                    <p className="mt-1 text-slate-500">Configuration globale de l'application</p>
                </div>
            </div>

            <Card className="bg-white rounded-xl border-2 border-[#2e7cd6]/40 shadow-sm max-w-5xl">
                <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-[#2e7cd6]/5 to-transparent">
                    <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                        <Calculator size={18} className="text-[#2e7cd6]" />
                        Formule de calcul du prix
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-5">
                    {!settings ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="h-6 w-6 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : (
                        <>
                            {/* Formule Total HT */}
                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="text-sm font-semibold text-slate-700">Total HT</Label>
                                    {!editing ? (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={startEdit}
                                            className="h-8 text-xs"
                                            data-testid="edit-formula-btn"
                                        >
                                            <Pencil size={14} className="mr-1.5" /> Modifier
                                        </Button>
                                    ) : (
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={cancelEdit}
                                                className="h-8 text-xs"
                                                data-testid="cancel-formula-btn"
                                            >
                                                <XIcon size={14} className="mr-1.5" /> Annuler
                                            </Button>
                                            <Button
                                                type="button"
                                                size="sm"
                                                onClick={handleSave}
                                                disabled={saving}
                                                className="h-8 text-xs bg-[#2e7cd6] hover:bg-[#2568b8]"
                                                data-testid="save-formula-btn"
                                            >
                                                {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />}
                                                Enregistrer
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {!editing ? (
                                    <div
                                        className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-mono text-slate-700 whitespace-pre-wrap leading-relaxed"
                                        data-testid="formula-display"
                                    >
                                        Total HT = {settings.formula_ht}
                                    </div>
                                ) : (
                                    <Textarea
                                        value={draftFormula}
                                        onChange={e => setDraftFormula(e.target.value)}
                                        className="font-mono text-sm min-h-[140px] bg-white border-[#2e7cd6]/40 focus:border-[#2e7cd6]"
                                        data-testid="formula-textarea"
                                    />
                                )}

                                <p className="text-[11px] text-slate-400 mt-2 italic">
                                    Total TTC = Total HT × (1 + TVA / 100)
                                </p>
                            </div>

                            {/* TVA */}
                            <div className="mb-6 max-w-xs">
                                <Label className="text-sm font-semibold text-slate-700">TVA (%)</Label>
                                {!editing ? (
                                    <div
                                        className="mt-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono text-slate-700"
                                        data-testid="tva-display"
                                    >
                                        {settings.tva_rate} %
                                    </div>
                                ) : (
                                    <Input
                                        type="number"
                                        step="any"
                                        value={draftTva}
                                        onChange={e => setDraftTva(e.target.value)}
                                        className="mt-1"
                                        data-testid="tva-input"
                                    />
                                )}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* ===================== SECTION DOCUMENTS ===================== */}
            <Card className="bg-white rounded-xl border-2 border-slate-200 shadow-sm max-w-5xl mt-8" data-testid="admin-documents-section">
                <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-slate-100/60 to-transparent">
                    <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                        <FileText size={18} className="text-slate-600" />
                        Documents
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-5 space-y-8">

                    {/* --- Assurance --- */}
                    <div data-testid="doc-assurance">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <FileCheck size={18} className="text-[#2e7cd6]" />
                                <h3 className="text-sm font-semibold text-slate-800">Assurance</h3>
                                <span className="text-[11px] text-slate-400 italic">PDF à importer</span>
                            </div>
                            <div className="flex gap-2">
                                <input
                                    ref={assuranceInputRef}
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    onChange={handleAssuranceUpload}
                                    className="hidden"
                                    data-testid="assurance-file-input"
                                />
                                {!assurance && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => assuranceInputRef.current?.click()}
                                        disabled={uploadingAssurance}
                                        className="h-8 text-xs"
                                        data-testid="assurance-upload-btn"
                                    >
                                        {uploadingAssurance ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />}
                                        Importer
                                    </Button>
                                )}
                            </div>
                        </div>

                        {assurance ? (
                            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="assurance-info">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="h-10 w-10 rounded-md bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                                        <FileText size={20} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-800 truncate" data-testid="assurance-filename">{assurance.filename}</p>
                                        <p className="text-[11px] text-slate-500">
                                            {formatSize(assurance.size)} · ajouté le {formatDate(assurance.uploaded_at)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-1.5 shrink-0">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => openPreview('assurance', 'Assurance')}
                                        className="h-8 text-xs"
                                        data-testid="assurance-view-btn"
                                    >
                                        <Eye size={14} className="mr-1.5" /> Voir
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={handleAssuranceDownload}
                                        className="h-8 text-xs"
                                        data-testid="assurance-download-btn"
                                    >
                                        <Download size={14} className="mr-1.5" /> Télécharger
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={handleAssuranceDelete}
                                        className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                        data-testid="assurance-delete-btn"
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg px-4 py-6 text-center text-sm text-slate-500" data-testid="assurance-empty">
                                Aucun document d'assurance importé
                            </div>
                        )}
                    </div>

                    <div className="border-t border-slate-100" />

                    {/* --- Présentation entreprise (PDF généré dynamiquement) --- */}
                    <div data-testid="doc-presentation">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FileText size={18} className="text-[#2e7cd6]" />
                                <h3 className="text-sm font-semibold text-slate-800">Présentation de l'entreprise</h3>
                            </div>
                            <Button
                                type="button"
                                size="sm"
                                onClick={() => openPreview('presentation', 'Présentation de l\'entreprise')}
                                className="h-9 text-sm bg-[#2e7cd6] hover:bg-[#2568b8]"
                                data-testid="presentation-open-btn"
                            >
                                <Eye size={15} className="mr-2" /> Présentation de l'entreprise
                            </Button>
                        </div>
                    </div>

                </CardContent>
            </Card>

            {/* Modale aperçu PDF */}
            <Dialog open={!!previewTitle} onOpenChange={(open) => { if (!open) closePreview(); }}>
                <DialogContent className="max-w-5xl h-[85vh] p-0 gap-0 flex flex-col" data-testid="pdf-preview-dialog">
                    <DialogHeader className="px-5 py-3 border-b border-slate-200 flex-row items-center justify-between space-y-0">
                        <DialogTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <FileText size={16} className="text-[#2e7cd6]" /> Aperçu — {previewTitle}
                        </DialogTitle>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={downloadFromPreview}
                            disabled={!previewUrl}
                            className="h-8 text-xs mr-8"
                            data-testid="preview-download-btn"
                        >
                            <Download size={14} className="mr-1.5" /> Télécharger
                        </Button>
                    </DialogHeader>
                    <div className="flex-1 bg-slate-100 overflow-hidden">
                        {previewLoading || !previewUrl ? (
                            <div className="flex items-center justify-center h-full text-slate-500 gap-2">
                                <Loader2 size={18} className="animate-spin" /> Chargement du PDF...
                            </div>
                        ) : (
                            <iframe
                                title="Aperçu PDF"
                                src={previewUrl}
                                className="w-full h-full border-0"
                                data-testid="pdf-preview-iframe"
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
